import { FieldsMap, ICropping, IField, IOmniaSettings, IParam, isUndefinedOrNull, ParamKey } from "../omnia"
import { createNewField, updateField } from "../strings"
import { IFieldsEditService, IFieldsService } from "../types"
import { BoundaryDrawingContext } from "./boundary/drawingContext"
import { EditCmd } from "./editCmd"


export abstract class EditCtrl<T extends IField | ICropping> extends omnia.shell.Component {

    abstract readonly errorMessages: { [key: string]: () => string }

    protected abstract setupTarget(): void
    protected abstract createBoundaryEditContext(): BoundaryDrawingContext
    protected abstract setMultipleTargets(value: boolean): void

    protected _create: boolean

    public target: T

    private _fieldsMap: FieldsMap
    protected _editBoundaryVm: BoundaryDrawingContext

    protected formErrorKeys: { [key: string]: boolean }

    public readonly form: ng.IFormController
    protected _showValidationErrors: boolean

    private _parameters: { [key in ParamKey]?: IParam[] } = {}

    protected _drawingSettings: IOmniaSettings

    protected _multipleTargets: boolean


    constructor(
        $scope: ng.IScope,
        protected readonly svc: IFieldsService,
        protected readonly editSvc: IFieldsEditService,
        protected readonly topo: omnia.map.IMapTopologyService,
        protected readonly readOnly: boolean) {

        super($scope)

        this._icon = "fas fa-pen"

        this._drawingSettings = svc.getSettings("fields.drawing")

        $scope.$watch("vm.form.$error", e => this.updateFormErrors(e), true)

    }

    protected dispose() {

        if (this._editBoundaryVm) {
            this._editBoundaryVm.dispose()
            this._editBoundaryVm = null
        }

        const map = this._fieldsMap
        if (map) {
            map.croppingLayer.selectable = true
        }

    }

    getFormField(name: string) {
        const form = this.form
        return form ? form[name] as ng.INgModelController : null
    }

    get title(): string {
        return this._create ? createNewField : updateField
    }

    get fieldsMap(): FieldsMap {
        return this._fieldsMap
    }

    set fieldsMap(value: FieldsMap) {
        this._fieldsMap = value
    }

    get editBoundaryViewModel(): BoundaryDrawingContext {
        return this._editBoundaryVm
    }

    get showValidationErrors(): boolean {
        return this._showValidationErrors
    }

    get boundaryChanging() {
        return this._editBoundaryVm && this._editBoundaryVm.isActive
    }

    get hideInputs() {
        return this.boundaryChanging
    }

    get disableEdits() {
        return this.boundaryChanging
    }

    get multipleTargets() {
        return this._multipleTargets
    }

    set multipleTargets(value) {
        this.setMultipleTargets(value)
    }

    hasCalculatedManureArea(): boolean {

        const { target } = this

        return target
            && (!isUndefinedOrNull(target.ManureSpreadAreaCalc))
            && (target.ManureSpreadAreaCalc == target.ManureSpreadArea)

    }

    canChangeManureSpreadArea(): boolean {
        if (this.disableEdits) return false
        if (this.hasCalculatedManureArea()) return false
        return true
    }

    protected setup() {

        const cmd = <EditCmd>(this.componentContext.execArguments || {})

        this.target = <T>(cmd.editTarget || {})

        this._create = cmd.create

        this.setupTarget()

        this.editSvc.startEditing(cmd.editCroppings, this.target)

        this.setupEditBoundaryViewModel()

    }

    private setupEditBoundaryViewModel() {

        if (!this._fieldsMap) {
            this._editBoundaryVm = null
            return
        }

        this._editBoundaryVm = this.createBoundaryEditContext()
    }

    getParameters(type: ParamKey): IParam[] {

        if (!this._parameters[type]) {
            this._parameters[type] = this.svc.getParameters(type) || []
        }

        return this._parameters[type]

    }

    cancelEdit() {

        // Ensure that if we enabled this, we disable it again.
        this.setMultipleTargets(false);

        this.editSvc.stopEditing()

        if (this._editBoundaryVm) {
            this._editBoundaryVm.cancelChanges()
        }

        super.cancel();

    }

    getTargets() {
        return [this.target]
    }

    hasTargets() {
        return this.getTargets().length > 0
    }


    protected revalidate() {
        // NOP
    }

    canCommit(): boolean {

        if (this.readOnly) {
            return false
        }

        const c = this._create || this.hasTargets()

        if (c) {
            this.revalidate()
            return this.form && this.form.$valid
        }

        return false

    }

    private updateFormErrors($error: any) {

        const keys: { [key: string]: boolean } = {}

        if ($error) {
            Object.keys($error).forEach(e => $error[e].forEach(c => keys[c.$name + "/" + e] = true))
        }

        this.formErrorKeys = Object.keys(keys).length == 0 ? null : keys

    }

    hasErrors(...fields: string[]): boolean {

        return fields.some(f => {
            var field = this.form[f]
            return (field && field.$error && (Object.keys(field.$error).some(k => field.$error[k])))
        })

    }

    getErrorMessage(key: string, prefix = "") {
        const { errorMessages } = this
        key = prefix ? `${prefix}/${key}` : key
        const p = errorMessages[key]
        return p ? p() : `Error: ${key}`
    }

    getFirstErrorMessage(errors: { [key: string]: boolean }, prefix = "") {

        if (errors) {

            const key = Object.keys(errors).find(p => (!p.startsWith("$")) && errors[p])

            if (key) {
                return this.getErrorMessage(key, prefix)
            }

        }

    }

}
